import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { INFINITE_CACHE_AND_STALE_TIME } from 'constants/index';
import type { SparkStep, SparkStepResponse } from 'types/SparkStep';
import {
  generateFilter,
  generateIncludeQuery,
  generateSortQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { useWorkspaceContext } from 'hooks/common';
import { useSparkStepsBySparkIDHelper } from 'stores/sparkBuilderStore';
import { PromptFunctions } from 'types/PromptFunctions';

const getSparkSteps = (
  currentWorkspaceID: string | undefined | null,
  sparkID: string | null
) => {
  if (!currentWorkspaceID) {
    return Promise.reject('workspace ID can not be null');
  }

  const queryArray = [
    generateFilter('sparkID', sparkID),
    generateSortQuery('stepOrder', 'Acs'),
    generateIncludeQuery(['promptFunction']),
  ];

  const url = generateURL(SPARK_RESOURCE_TYPES.SPARK_STEPS, queryArray);
  return fetchWrapper<SparkStepResponse>({
    url: url,
    method: 'GET',
  });
};

export function useSparkStepsBySparkID(sparkID: string | null) {
  const sparkStepsBySparkIDHelper = useSparkStepsBySparkIDHelper();

  const { currentWorkspaceID } = useWorkspaceContext();
  const { data, ...rest } = useQuery(
    ['sparkSteps', sparkID],
    () => getSparkSteps(currentWorkspaceID, sparkID),
    {
      enabled: Boolean(sparkID && currentWorkspaceID),

      onSuccess: (data) => {
        sparkStepsBySparkIDHelper.onSuccess(data.data);
      },
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`failed to Fetch Spark Steps: ${error.message}`);
        }
      },
      refetchOnMount: false,
      ...INFINITE_CACHE_AND_STALE_TIME,
    }
  );

  const { data: sparkSteps, included, ...restData } = data || {};

  const sparkStepsByIdMap = sparkSteps?.reduce<Record<string, SparkStep>>(
    (acc, step) => {
      return { ...acc, [step.id]: step };
    },
    {}
  );
  const promptFunctionsByIdMap = included?.reduce<
    Record<string, PromptFunctions>
  >((acc, promptFunction) => {
    return { ...acc, [promptFunction.id]: promptFunction };
  }, {});

  return {
    sparkStepsByIdMap: sparkStepsByIdMap,
    sparkSteps: sparkSteps,
    promptFunctionsByIdMap,
    ...restData,
    ...rest,
  };
}
